import { useEffect, useState } from "react";
import api, { formatFCFA } from "@/lib/api";
import { Plus, Trash2, Tag } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminPromotions() {
  const [promos, setPromos] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", discount_type: "percent", discount_value: 10, description: "" });

  const load = () => api.get("/admin/promotions").then((r) => setPromos(r.data));
  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    try {
      await api.post("/admin/promotions", { ...form, discount_value: parseFloat(form.discount_value) });
      toast.success("Code promo créé");
      setOpen(false);
      setForm({ code: "", discount_type: "percent", discount_value: 10, description: "" });
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const onDelete = async (id) => {
    await api.delete(`/admin/promotions/${id}`);
    toast.success("Code supprimé");
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-promotions">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-4xl text-[var(--dsf-green)]">Promotions</h1>
          <p className="text-[var(--dsf-text-muted)] text-sm mt-1">Codes promo, ventes flash et campagnes</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary" data-testid="add-promo-button"><Plus size={14} /> Nouveau code</button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif text-2xl">Nouveau code promo</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Code</Label>
                <Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} className="mt-1 uppercase" data-testid="promo-form-code" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Type</Label>
                <select value={form.discount_type} onChange={(e) => setForm({...form, discount_type: e.target.value})}
                  className="w-full border border-[var(--dsf-border)] px-4 py-2 mt-1 text-sm" data-testid="promo-form-type">
                  <option value="percent">Pourcentage</option>
                  <option value="fixed">Montant fixe (FCFA)</option>
                </select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Valeur</Label>
                <Input type="number" value={form.discount_value} onChange={(e) => setForm({...form, discount_value: e.target.value})} className="mt-1" data-testid="promo-form-value" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Description</Label>
                <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="btn-outline-dark">Annuler</button>
              <button onClick={onCreate} className="btn-primary" data-testid="promo-form-submit">Créer</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {promos.map((p) => (
          <div key={p.id} className="bg-white border border-[var(--dsf-border)] p-6" data-testid={`promo-card-${p.code}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Tag size={14} className="text-[var(--dsf-orange)]" />
                  <span className="font-serif text-2xl font-semibold text-[var(--dsf-green)]">{p.code}</span>
                </div>
                <div className="text-sm text-[var(--dsf-text-secondary)]">{p.description}</div>
                <div className="mt-3">
                  <span className="bg-[var(--dsf-burgundy)] text-white text-[10px] uppercase tracking-wider px-2 py-1 font-semibold">
                    {p.discount_type === "percent" ? `-${p.discount_value}%` : `-${formatFCFA(p.discount_value)}`}
                  </span>
                </div>
              </div>
              <button onClick={() => onDelete(p.id)} className="text-[var(--dsf-text-muted)] hover:text-[var(--dsf-burgundy)]" data-testid={`delete-promo-${p.code}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
